import Cookies from "js-cookie";
import jwt_decode from "jwt-decode";

// Central auth-session helpers: safe JWT decoding / exp validation and ONE logout
// used by the sidebar, manage-account, expiry timer, 401 interceptors and other tabs.
// This file intentionally has no React or axios import so it can be unit tested alone.

// setTimeout stores its delay as a signed 32-bit int; anything larger fires immediately.
export const MAX_TIMEOUT_MS = 2147483647;
export const LOGOUT_EVENT_KEY = "logout-event";

// Endpoints whose 401 means "wrong credentials / bad link", not "session expired".
const AUTH_ENDPOINTS = ["/api/v2/student/login", "/api/auth/"];

let loggedOut = false;
let logoutHandler = null;

// Decodes a JWT without ever throwing. Returns the payload or null.
export function decodeToken(token) {
	if (!token || token === "null" || token === "undefined") return null;
	try {
		return jwt_decode(token);
	} catch (error) {
		return null;
	}
}

// Token expiry in epoch milliseconds, or null when the token is malformed / has no exp.
export function getTokenExpiryMs(token) {
	const payload = decodeToken(token);
	if (!payload || typeof payload.exp !== "number") return null;
	return payload.exp * 1000;
}

// A token is valid only if it decodes and its exp is still in the future.
export function isTokenValid(token, now = Date.now()) {
	const expiryMs = getTokenExpiryMs(token);
	return expiryMs !== null && expiryMs > now;
}

export function clearStoredSession() {
	Cookies.remove("authToken");
	try {
		localStorage.removeItem("authToken");
		localStorage.removeItem("expirationTime");
		localStorage.removeItem("studentId");
	} catch (error) {
		// storage unavailable - nothing more to clear
	}
}

// Startup restore: returns a non-expired token from the cookie or (keep me logged in)
// localStorage, otherwise clears everything and returns null. Never restores an
// expired or malformed token.
export function getInitialAuthToken() {
	const cookieToken = Cookies.get("authToken");
	if (isTokenValid(cookieToken)) return cookieToken;
	let storedToken = null;
	try {
		storedToken = localStorage.getItem("authToken");
	} catch (error) {
		storedToken = null;
	}
	if (isTokenValid(storedToken)) {
		Cookies.set("authToken", storedToken);
		return storedToken;
	}
	clearStoredSession();
	return null;
}

// AuthContext registers what "clear the context user" means (dispatch LOGOUT).
export function setLogoutHandler(handler) {
	logoutHandler = handler;
}

// Call after a successful login so a later logout can run again.
export function resetLogout() {
	loggedOut = false;
}

// The single logout. Idempotent until resetLogout(). Clearing the context user makes the
// route guards in App.js redirect to /login (user is cleared first, so /login does not
// bounce back to /home).
export function logout({ broadcast = true } = {}) {
	if (loggedOut) return;
	loggedOut = true;
	try {
		if (logoutHandler) logoutHandler();
	} finally {
		clearStoredSession();
		if (broadcast) {
			try {
				localStorage.setItem(LOGOUT_EVENT_KEY, String(Date.now()));
			} catch (error) {
				// storage unavailable - other tabs just won't be notified
			}
		}
	}
}

function hasAuthorizationHeader(config) {
	const headers = config && config.headers;
	if (!headers) return false;
	const value =
		typeof headers.get === "function"
			? headers.get("Authorization")
			: headers.Authorization || headers.authorization;
	return Boolean(value);
}

// Response-error handler: a 401 on a request that carried a Bearer token (and is not a
// login/forgot/reset call) ends the session once, then the original error is re-thrown.
export function handleUnauthorized(error) {
	const config = error && error.config;
	const status = error && error.response && error.response.status;
	if (
		status === 401 &&
		hasAuthorizationHeader(config) &&
		!AUTH_ENDPOINTS.some((endpoint) => String(config.url || "").includes(endpoint))
	) {
		logout();
	}
	return Promise.reject(error);
}

const installed = new WeakSet();
export function installAuthInterceptor(axiosInstance) {
	if (installed.has(axiosInstance)) return;
	installed.add(axiosInstance);
	axiosInstance.interceptors.response.use((response) => response, handleUnauthorized);
}
